import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { UntypedFormControl, FormGroupDirective } from "@angular/forms";
import { FormGroup, FormText, TextBoxText } from "@coso/forms";
import { ILibraryDistribution, LibraryRule, LibraryRuleName, Period, PeriodName, PeriodSource, PeriodSourceName } from "../../../../models/auto-generated";
import { BehaviorSubject } from "rxjs";

@Component({
  standalone:false,
  templateUrl: "./library-settings.component.html",
  styleUrls: ["./library-settings.component.scss"]
})
export class LibrarySettingsComponent implements OnInit {
  @ViewChild("patternPopover", { static: true }) patternPopover: TemplateRef<any>;
  @ViewChild("periodPopover", { static: true }) periodPopover: TemplateRef<any>;
  @ViewChild("periodSourcePopover", { static: true }) periodSourcePopover: TemplateRef<any>;
  @ViewChild("libraryNamePopover", { static: true }) libraryNamePopover: TemplateRef<any>;
  @ViewChild("characterCountPopover", { static: true }) characterCountPopover: TemplateRef<any>;
  LibraryRule = LibraryRule;
  Period = Period;
  formGroup: FormGroup<ILibraryDistribution>;
  formText: FormText<ILibraryDistribution>;
  folderNamingPatternFormControl: UntypedFormControl;
  folderNamingPatternInputText: TextBoxText;
  isReadOnly = new BehaviorSubject<boolean>(false);

  get isReadOnly$() {
    return this.isReadOnly.asObservable();
  }

  constructor(private formGroupDirective: FormGroupDirective) {}

  ngOnInit(): void {
    this.folderNamingPatternFormControl = this.formGroupDirective.control.get("settings").get("folderNamingPattern") as UntypedFormControl;
    this.folderNamingPatternInputText = {
      label: "Record Name (library part)",
      labelClass: "col-lg-3",
      inputContainerClass: "col-lg-5",
      popover: "The name of the folder to be created. The following placeholders can be used: {0} for the name of the object and {1} for the id of the object.",
      errors: {
        required: "The Record Name field is required."
      }
    };
    this.formGroup = this.formGroupDirective.control.get("libraryDistribution") as FormGroup<ILibraryDistribution>;
    this.formText = {
      pattern: {
        label: "Library Naming Rule",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: this.libraryNamePopover,
        options: Object.keys(LibraryRule)
          .filter((k) => isNaN(Number(k)))
          .map((k) => ({ label: LibraryRuleName.get(LibraryRule[k]), value: LibraryRule[k] }))
      },
      libraryTemplate: {
        label: "Library Template Id",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "The template id used to create document libraries in SharePoint. The default value is 101."
      },
      libraryTemplateName: {
        label: "Library Template Name",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "The template name used to create document libraries in SharePoint."
      },
      period: {
        label: "Period",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: this.periodPopover,
        options: [
          { label: "Select a period", value: null, disabled: true },
          ...Object.keys(Period)
            .filter((k) => isNaN(Number(k)))
            .map((k) => ({ label: PeriodName.get(Period[k]), value: Period[k] }))
        ],
        errors: {
          required: "The Period field is required."
        }
      },
      periodSource: {
        label: "Period Data Source",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: this.periodSourcePopover,
        options: Object.keys(PeriodSource)
          .filter((k) => isNaN(Number(k)))
          .map((k) => ({ label: PeriodSourceName.get(PeriodSource[k]), value: PeriodSource[k] }))
      },
      namePattern: {
        label: "Library Name",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: this.libraryNamePopover,
        errors: {
          required: "The Library Name field is required."
        }
      },
      characterCount: {
        label: "Number of Characters",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: this.characterCountPopover,
        errors: {
          required: "The Number of Characters field is required.",
          min: "The Number of Characters field must be greater than or equal to 1.",
          max: "The Number of Characters field must be less than or equal to 16.",
          pattern: "The Number of Characters field must contain only numbers."
        }
      },
      unknownCharacter: {
        label: "Unknown Character",
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-5",
        popover: "The character used to replace characters that are non-alphanumeric in the document library name.",
        errors: {
          required: "The Unknown Character field is required.",
          maxlength: "The maximum length allowed in the Unknown Character field is 1 characters."
        }
      }
    };
  }
}
